import React from 'react';
import { useTranslation } from 'react-i18next';
import { ErrorBoundary } from '@/components/errorBoundary';
import type { ErrorResponse } from '@/api/handlers/responses';
import { ActiveDraftsSkeleton } from './ActiveDraftsSkeleton';
import { ActiveDraftsView } from './ActiveDraftsView';
import type { ActiveDraft } from './activeDraftsMeta';
import { useActiveDrafts } from './useActiveDrafts';

type ActiveDraftsContentProps = {
    error: ErrorResponse | null;
    isLoading: boolean;
    items: ActiveDraft[];
    onDelete: (draftId: string) => void;
    onEdit: (draftId: string) => void;
};

/**
 * Self-served Active-Drafts widget. Same throw-in-child container
 * pattern as the other invoice widgets so the page just drops it in.
 */
export const ActiveDrafts: React.FC = () => {
    const { data, error, isLoading, onDelete, onEdit, onRetry } =
        useActiveDrafts();
    const { t } = useTranslation('pages');

    const copy = React.useMemo(
        () => ({
            errorTitle: t('purchases.widgets.activeDrafts.errorTitle'),
            errorMessage: t('purchases.widgets.activeDrafts.errorMessage'),
            errorRetry: t('purchases.widgets.activeDrafts.errorRetry'),
        }),
        [t],
    );

    return (
        <ErrorBoundary
            data-testid="active-drafts-error-boundary"
            fallbackMessage={copy.errorMessage}
            id="active-drafts-error-boundary"
            onRetry={onRetry}
            resetKeys={[error]}
            retryLabel={copy.errorRetry}
            title={copy.errorTitle}
        >
            <ActiveDraftsContent
                error={error}
                isLoading={isLoading}
                items={data}
                onDelete={onDelete}
                onEdit={onEdit}
            />
        </ErrorBoundary>
    );
};

const ActiveDraftsContent: React.FC<ActiveDraftsContentProps> = ({
    error,
    isLoading,
    items,
    onDelete,
    onEdit,
}) => {
    if (error && items.length === 0) {
        throw error;
    }

    if (isLoading && items.length === 0) {
        return <ActiveDraftsSkeleton />;
    }

    return (
        <ActiveDraftsView items={items} onDelete={onDelete} onEdit={onEdit} />
    );
};
